import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { kpiMeta } from '../config/kpi-config';
import { formatValue } from '../utils/formatters';
import { triggerHaptic } from '../utils/haptics';
import DeltaChip from './DeltaChip';
import EnhancedSparkline from './EnhancedSparkline';

interface MetricCardProps extends Omit<HTMLMotionProps<"button">, "children" | "onClick"> {
  kpiId: string;
  latestValue: number | null;
  prevYearValue: number | null;
  timeseries: Array<{ year: number; value: number; prevYearValue: number | null }>;
  isActive: boolean;
  onClick: () => void;
  compact?: boolean;
}

const MetricCard = React.forwardRef<HTMLButtonElement, MetricCardProps>(({
  kpiId,
  latestValue,
  prevYearValue,
  timeseries,
  isActive,
  onClick,
  compact = false,
  className,
  ...props
}, ref) => {
  const meta = kpiMeta[kpiId];

  if (!meta) {
    return null; // Skip unknown KPIs
  }

  // Get start and end years for sparkline labels
  const startYear = timeseries.length > 0 ? timeseries[0].year : null;
  const endYear = timeseries.length > 0 ? timeseries[timeseries.length - 1].year : null;

  // Calculate trend sentiment for visual harmonization
  const delta = prevYearValue !== null ? (latestValue || 0) - prevYearValue : 0;
  const isGoodTrend = meta.higherIsBetter === null ? null : ((delta > 0) === meta.higherIsBetter);
  const hasNegativeTrend = prevYearValue !== null && Math.abs(delta) >= 0.1 && isGoodTrend === false;

  return (
    <motion.button
      ref={ref}
      layout
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`
        w-full rounded-2xl border transition-all duration-300 
        focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2
        ${compact ? 'p-3 min-h-[110px]' : 'p-5 min-h-[140px]'}
        ${isActive
          ? "bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 dark:from-emerald-500/20 dark:to-emerald-900/10 border-emerald-500/50 shadow-[0_8px_30px_rgb(16,185,129,0.1)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] ring-1 ring-emerald-500/20"
          : hasNegativeTrend
            ? "bg-rose-50/50 dark:bg-rose-900/10 border-rose-100 dark:border-rose-800/50 hover:border-rose-200 dark:hover:border-rose-700 shadow-sm"
            : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
        }
        ${className || ''}
      `}
      onClick={() => { triggerHaptic('light'); onClick(); }}
      aria-selected={isActive}
      aria-label={`${meta.name}: ${formatValue(latestValue || 0, meta)}. ${prevYearValue !== null ? `Förändring: ${formatValue((latestValue || 0) - prevYearValue, meta)}` : 'Ingen tidigare data'}. Tryck för att se detaljerad analys.`}
      role="option"
      tabIndex={0}
      aria-describedby={`${kpiId}-description`}
      {...props}
    >
      {/* Card content with clear hierarchy */}
      <div className={`flex flex-col h-full justify-between ${compact ? 'gap-2' : 'space-y-3'}`}>
        {/* 1. Metric Title */}
        <div className="text-left w-full">
          <h3 className={`${compact ? 'text-sm ell-2' : 'text-base ell-2'} font-semibold text-slate-900 dark:text-slate-100 leading-tight`} title={meta.name}>
            {meta.name}
          </h3>
          {!compact && (
            <div className="mt-1 flex items-center gap-2 text-mobile-sm text-slate-500 dark:text-slate-400">
              <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono text-xs ui-label" aria-label={`KPI-kod: ${kpiId}`}>
                {kpiId}
              </span>
              <span className="ui-label">• {meta.unit}</span>
            </div>
          )}
        </div>

        {/* 2. Primary Value */}
        <div className="text-center">
          <div className={`${compact ? 'text-2xl' : 'text-3xl'} font-bold tabular-nums text-slate-900 dark:text-slate-100`}>
            {formatValue(latestValue || 0, meta)}
          </div>
        </div>

        {/* 3. Trend Indicator */}
        <div className="flex justify-center">
          <DeltaChip
            value={latestValue || 0}
            prevValue={prevYearValue}
            meta={meta}
            compact={compact}
          />
        </div>

        {/* 4. Sparkline - Hidden in compact mode */}
        {!compact && (
          <div className="space-y-1">
            <EnhancedSparkline timeseries={timeseries} meta={meta} />
            {startYear && endYear && startYear !== endYear && (
              <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500">
                <span>{startYear}</span>
                <span>{endYear}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.button>
  );
});

MetricCard.displayName = 'MetricCard';

export default MetricCard;
